import * as THREE from 'three';

/** Bryce Canyon (Sunset Point) geodetic position. */
export const SITE = { lat: 37.6228, lon: -112.1661, utmZoneCentralLon: -111 };
const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;
// UTM grid convergence: grid north is rotated from true north by gamma.
const GRID_CONVERGENCE = (SITE.lon - SITE.utmZoneCentralLon) * Math.sin(SITE.lat * D2R);

export interface CelestialState {
  sunDir: THREE.Vector3;
  sunAlt: number; // degrees, apparent (with refraction)
  sunAz: number; // degrees from true north, clockwise
  moonDir: THREE.Vector3;
  moonAlt: number;
  moonIllum: number; // illuminated fraction 0..1
  moonPhaseAngle: number; // 0 = new, 180 = full (degrees of elongation)
  lst: number; // local sidereal time (radians)
}

function norm360(x: number) {
  return ((x % 360) + 360) % 360;
}

/** Converts horizontal coordinates to a scene-frame unit vector (x east, y up, z south). */
export function horizToVec(altDeg: number, azDeg: number, out = new THREE.Vector3()) {
  const az = (azDeg - GRID_CONVERGENCE) * D2R; // to grid azimuth
  const alt = altDeg * D2R;
  return out.set(Math.sin(az) * Math.cos(alt), Math.sin(alt), -Math.cos(az) * Math.cos(alt));
}

function refraction(altDeg: number) {
  // Bennett's formula (arc-minutes), valid down to slightly below the horizon
  if (altDeg < -1.5) return 0;
  const a = Math.max(altDeg, -1.0);
  return 1 / Math.tan((a + 7.31 / (a + 4.4)) * D2R) / 60;
}

function equToHoriz(raDeg: number, decDeg: number, lstDeg: number) {
  const ha = (lstDeg - raDeg) * D2R;
  const dec = decDeg * D2R;
  const lat = SITE.lat * D2R;
  const sinAlt = Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(ha);
  const alt = Math.asin(Math.max(-1, Math.min(1, sinAlt)));
  const az = Math.atan2(Math.sin(ha), Math.cos(ha) * Math.sin(lat) - Math.tan(dec) * Math.cos(lat)) + Math.PI;
  return { alt: alt * R2D, az: norm360(az * R2D) };
}

export function computeCelestial(date: Date): CelestialState {
  const jd = date.getTime() / 86400000 + 2440587.5;
  const n = jd - 2451545.0;
  // --- Sun (low precision, ~0.01 deg)
  const L = norm360(280.46 + 0.9856474 * n);
  const g = norm360(357.528 + 0.9856003 * n) * D2R;
  const lambda = (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * D2R;
  const eps = (23.439 - 0.0000004 * n) * D2R;
  const ra = norm360(Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda)) * R2D);
  const dec = Math.asin(Math.sin(eps) * Math.sin(lambda)) * R2D;
  const gmst = norm360((18.697374558 + 24.06570982441908 * n) * 15);
  const lst = norm360(gmst + SITE.lon);
  const sh = equToHoriz(ra, dec, lst);
  const sunAlt = sh.alt + refraction(sh.alt);

  // --- Moon (Schlyter's method with main perturbations, ~0.2 deg)
  const d = jd - 2451543.5;
  const N = norm360(125.1228 - 0.0529538083 * d) * D2R;
  const inc = 5.1454 * D2R;
  const w = norm360(318.0634 + 0.1643573223 * d) * D2R;
  const a = 60.2666;
  const e = 0.0549;
  const M = norm360(115.3654 + 13.0649929509 * d) * D2R;
  let E = M + e * Math.sin(M) * (1 + e * Math.cos(M));
  for (let i = 0; i < 5; i++) E = E - (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
  const xv = a * (Math.cos(E) - e);
  const yv = a * Math.sqrt(1 - e * e) * Math.sin(E);
  const v = Math.atan2(yv, xv);
  const r = Math.hypot(xv, yv);
  const xh = r * (Math.cos(N) * Math.cos(v + w) - Math.sin(N) * Math.sin(v + w) * Math.cos(inc));
  const yh = r * (Math.sin(N) * Math.cos(v + w) + Math.cos(N) * Math.sin(v + w) * Math.cos(inc));
  const zh = r * Math.sin(v + w) * Math.sin(inc);
  let lonEcl = Math.atan2(yh, xh) * R2D;
  let latEcl = Math.atan2(zh, Math.hypot(xh, yh)) * R2D;
  // perturbations
  const Ms = g; // sun mean anomaly
  const Lm = N + w + M;
  const Ls = (L * D2R);
  const Dm = Lm - Ls;
  const F = Lm - N;
  lonEcl += -1.274 * Math.sin(M - 2 * Dm) + 0.658 * Math.sin(2 * Dm) - 0.186 * Math.sin(Ms)
    - 0.059 * Math.sin(2 * M - 2 * Dm) - 0.057 * Math.sin(M - 2 * Dm + Ms) + 0.053 * Math.sin(M + 2 * Dm)
    + 0.046 * Math.sin(2 * Dm - Ms) + 0.041 * Math.sin(M - Ms) - 0.035 * Math.sin(Dm) - 0.031 * Math.sin(M + Ms);
  latEcl += -0.173 * Math.sin(F - 2 * Dm) - 0.055 * Math.sin(M - F - 2 * Dm) - 0.046 * Math.sin(M + F - 2 * Dm);
  const lo = lonEcl * D2R, la = latEcl * D2R;
  const xe = Math.cos(lo) * Math.cos(la);
  const ye = Math.sin(lo) * Math.cos(la) * Math.cos(eps) - Math.sin(la) * Math.sin(eps);
  const ze = Math.sin(lo) * Math.cos(la) * Math.sin(eps) + Math.sin(la) * Math.cos(eps);
  const mra = norm360(Math.atan2(ye, xe) * R2D);
  const mdec = Math.atan2(ze, Math.hypot(xe, ye)) * R2D;
  const mh = equToHoriz(mra, mdec, lst);
  const parallax = Math.asin(1 / r) * R2D;
  let moonAlt = mh.alt - parallax * Math.cos(mh.alt * D2R);
  moonAlt += refraction(moonAlt);
  const elong = Math.acos(Math.cos((lonEcl - L) * D2R) * Math.cos(latEcl * D2R)) * R2D;

  return {
    sunDir: horizToVec(sunAlt, sh.az),
    sunAlt,
    sunAz: sh.az,
    moonDir: horizToVec(moonAlt, mh.az),
    moonAlt,
    moonIllum: (1 - Math.cos(elong * D2R)) / 2,
    moonPhaseAngle: elong,
    lst: lst * D2R,
  };
}

/** US Mountain Time (America/Denver) offset in hours for a given local calendar date. */
export function mountainOffsetHours(year: number, month: number, day: number, hour: number): number {
  // DST: second Sunday in March 2:00 -> first Sunday in November 2:00
  const nthSunday = (m: number, nth: number) => {
    const first = new Date(Date.UTC(year, m, 1)).getUTCDay();
    return 1 + ((7 - first) % 7) + (nth - 1) * 7;
  };
  const dstStart = nthSunday(2, 2);
  const dstEnd = nthSunday(10, 1);
  const md = month * 100 + day + hour / 100;
  const inDst = md >= 2 * 100 + dstStart + 0.02 && md < 10 * 100 + dstEnd + 0.02;
  return inDst ? -6 : -7;
}

/** Builds a UTC Date from a Bryce-local date and fractional hour. */
export function localToUTC(year: number, month: number, day: number, hourFloat: number): Date {
  const off = mountainOffsetHours(year, month, day, hourFloat);
  const ms = Date.UTC(year, month, day) + (hourFloat - off) * 3600000;
  return new Date(ms);
}

export { GRID_CONVERGENCE };
